import React, { useState, useEffect } from 'react';
import { CreditCard, Search, DollarSign, Calendar, User, Mail, Phone, RefreshCw, XCircle, CheckCircle, Clock, Eye, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentsTab = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [events, setEvents] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchEvents();
  }, [statusFilter, eventFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (eventFilter) params.append('event_id', eventFilter);
      
      const response = await axios.get(`${API}/api/admin/orders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.items || []);
      setTotalRevenue(response.data.total_revenue || 0);
    } catch (error) {
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.items || []);
    } catch (error) {
      console.error('Error fetching events');
    }
  };

  const handleRefund = async (orderId) => {
    const reason = prompt('Razón del reembolso:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/api/admin/orders/${orderId}/refund`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reembolso procesado');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar reembolso');
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/api/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrder(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusColors = {
    paid: 'bg-green-500/20 text-green-400',
    pending: 'bg-yellow-500/20 text-yellow-400',
    refunded: 'bg-purple-500/20 text-purple-400',
    cancelled: 'bg-red-500/20 text-red-400',
    failed: 'bg-red-500/20 text-red-400'
  };

  const statusLabels = {
    paid: 'Pagado',
    pending: 'Pendiente',
    refunded: 'Reembolsado',
    cancelled: 'Cancelado',
    failed: 'Fallido'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <CreditCard className="w-6 h-6 mr-2 text-amber-500" />
            Pagos y Órdenes
          </h2>
          <p className="text-gray-400 text-sm">Gestiona transacciones y reembolsos</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-gray-400 text-sm">Ingresos totales</p>
          <p className="text-2xl font-bold text-green-400">${totalRevenue.toLocaleString()} MXN</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700"
        >
          <option value="">Todos los estados</option>
          <option value="paid">Pagados</option>
          <option value="pending">Pendientes</option>
          <option value="refunded">Reembolsados</option>
        </select>
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700"
        >
          <option value="">Todos los eventos</option>
          {events.map(event => (
            <option key={event.event_id} value={event.event_id}>{event.name?.es}</option>
          ))}
        </select>
        <button
          onClick={fetchOrders}
          className="flex items-center px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </button>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl">
          <CreditCard className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-gray-400">No hay órdenes</p>
        </div>
      ) : (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Orden</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Cliente</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Evento</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Tickets</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Total</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Estado</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Fecha</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.order_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                    <td className="p-4">
                      <p className="text-white font-mono text-sm">{order.order_id}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-white">{order.customer_name}</p>
                      <p className="text-gray-500 text-sm">{order.customer_email}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-300">{order.event_name}</p>
                      <p className="text-gray-500 text-sm">{order.access_level_name}</p>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-white">{order.num_attendees}</span>
                    </td>
                    <td className="p-4">
                      <p className="text-amber-400 font-medium">${order.total_amount?.toLocaleString()}</p>
                      <p className="text-gray-500 text-sm">{order.currency}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusColors[order.payment_status] || 'bg-gray-500/20 text-gray-400'}`}>
                        {statusLabels[order.payment_status] || order.payment_status}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-400 text-sm">{formatDate(order.created_at)}</p>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewOrderDetails(order.order_id)}
                          className="p-2 text-gray-400 hover:text-white"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {order.payment_status === 'paid' && (
                          <button
                            onClick={() => handleRefund(order.order_id)}
                            className="p-2 text-gray-400 hover:text-red-500"
                            title="Reembolsar"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-zinc-900 rounded-2xl max-w-2xl w-full border border-zinc-800" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Detalles de Orden</h3>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">ID Orden</p>
                  <p className="text-white font-mono">{selectedOrder.order_id}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Estado</p>
                  <span className={`px-2 py-1 rounded-full text-xs ${statusColors[selectedOrder.payment_status]}`}>
                    {statusLabels[selectedOrder.payment_status]}
                  </span>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total</p>
                  <p className="text-amber-400 font-bold text-xl">${selectedOrder.total_amount?.toLocaleString()} {selectedOrder.currency}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Fecha</p>
                  <p className="text-white">{formatDate(selectedOrder.created_at)}</p>
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="bg-zinc-800 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Cliente</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-300">
                    <User className="w-4 h-4 mr-2 text-amber-500" />
                    {selectedOrder.customer_name}
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Mail className="w-4 h-4 mr-2 text-amber-500" />
                    {selectedOrder.customer_email}
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Phone className="w-4 h-4 mr-2 text-amber-500" />
                    {selectedOrder.customer_whatsapp}
                  </div>
                </div>
              </div>
              
              {/* Tickets */}
              {selectedOrder.tickets?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3">Tickets ({selectedOrder.tickets.length})</h4>
                  <div className="space-y-2">
                    {selectedOrder.tickets.map((ticket) => (
                      <div key={ticket.ticket_id} className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {ticket.qr_code && (
                            <img 
                              src={`data:image/png;base64,${ticket.qr_code}`}
                              alt="QR"
                              className="w-12 h-12 bg-white rounded p-0.5"
                            />
                          )}
                          <div>
                            <p className="text-white text-sm">Ticket #{ticket.attendee_number}</p>
                            <p className="text-gray-500 text-xs font-mono">{ticket.ticket_id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {ticket.checked_in ? (
                            <span className="text-green-400 text-sm flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Check-in
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              Pendiente
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsTab;
